import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { KeyRound, Loader2, CheckCircle, ShieldAlert } from 'lucide-react';
import { sevBadge, CostBadge, ScoreRing, handleApiError } from './shared';
import { EntraConnector } from './EntraConnector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EntraIdAssessment = ({ locked, cost, onComplete }) => {
  const navigate = useNavigate();
  const [controls, setControls] = useState([]);
  const [tenant, setTenant] = useState({ tenant_name: '', domain: '' });
  const [answers, setAnswers] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { axios.get(`${API}/org-security/entra-id/controls`, { withCredentials: true }).then(r => setControls(r.data)).catch(() => {}); }, []);

  const categories = [...new Set(controls.map(c => c.category))];

  const assess = async () => {
    if (!tenant.tenant_name) { toast.error('Tenant name is required'); return; }
    setRunning(true);
    try {
      const r = await axios.post(`${API}/org-security/entra-id/assess`, { ...tenant, answers }, { withCredentials: true });
      setResult(r.data); onComplete?.(); toast.success(`Entra ID posture score: ${r.data.score}/100`);
    } catch (err) { handleApiError(err, toast, navigate); } finally { setRunning(false); }
  };

  return (
    <div className="space-y-6" data-testid="entra-id-module">
      <EntraConnector locked={locked} cost={cost} onResult={(r) => { setResult(r); onComplete?.(); }} />
      <div className="flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />or assess manually with the checklist<span className="h-px flex-1 bg-border" /></div>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base font-heading flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" /> Tenant details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label className="mb-2 block">Tenant name</Label><Input value={tenant.tenant_name} onChange={e => setTenant(t => ({ ...t, tenant_name: e.target.value }))} placeholder="Contoso Ltd" data-testid="entra-tenant-input" /></div>
          <div><Label className="mb-2 block">Primary domain</Label><Input value={tenant.domain} onChange={e => setTenant(t => ({ ...t, domain: e.target.value }))} placeholder="contoso.onmicrosoft.com" data-testid="entra-domain-input" /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Security control checklist <span className="text-xs text-muted-foreground font-normal ml-2">Toggle each control that is enforced in your tenant</span></CardTitle></CardHeader>
        <CardContent className="space-y-5">
          {categories.map(cat => (
            <div key={cat}>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{cat}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {controls.filter(c => c.category === cat).map(c => (
                  <div key={c.id} className="flex items-start justify-between gap-3 p-3 rounded-lg border bg-card" data-testid={`entra-control-${c.id}`}>
                    <div className="min-w-0"><div className="text-sm font-medium flex items-center gap-2">{c.name}<Badge variant="outline" className={`text-[9px] uppercase ${sevBadge[c.severity]}`}>{c.severity}</Badge></div><p className="text-xs text-muted-foreground mt-0.5">{c.description}</p></div>
                    <Switch checked={!!answers[c.id]} onCheckedChange={v => setAnswers(a => ({ ...a, [c.id]: v }))} disabled={locked} data-testid={`entra-toggle-${c.id}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <Button onClick={assess} disabled={locked || running} className="gap-2" data-testid="entra-assess-btn">{running ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />} Run Entra ID assessment <CostBadge cost={cost} /></Button>
        </CardContent>
      </Card>

      {result && (
        <Card className="border-primary/30" data-testid="entra-result">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading flex items-center gap-2 flex-wrap">Posture report — {result.name} <Badge variant="outline" className="text-[10px]">{result.method === 'live' ? 'Live · Microsoft Graph' : 'Questionnaire'}</Badge></CardTitle>
            {result.tenant_summary && <p className="text-xs text-muted-foreground" data-testid="entra-tenant-summary">{result.tenant_summary.ca_policies_enabled ?? '?'} / {result.tenant_summary.ca_policies_total ?? '?'} CA policies enabled · Security Defaults {result.tenant_summary.security_defaults ? 'on' : 'off'} · {result.tenant_summary.global_admins ?? '?'} Global Admins{result.tenant_summary.unreadable?.length ? ` · unreadable: ${result.tenant_summary.unreadable.join(', ')}` : ''}</p>}
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-center">
              <ScoreRing score={result.score} label={`Grade ${result.grade}`} />
              {['critical', 'high', 'medium', 'low'].map(s => <div key={s} className={`text-center p-3 rounded-lg ${sevBadge[s]}`}><p className="text-xl font-bold">{result.severity_counts[s]}</p><p className="text-[10px] capitalize">{s}</p></div>)}
              <div className="text-center p-3 rounded-lg bg-green-500/10 text-green-500"><p className="text-xl font-bold">{result.passed_controls.length}</p><p className="text-[10px]">Passed</p></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(result.category_breakdown).map(([cat, v]) => <div key={cat} className="p-2.5 rounded-lg border text-xs flex justify-between"><span className="text-muted-foreground">{cat}</span><span className="font-mono">{v.passed}/{v.total}</span></div>)}
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Findings ({result.findings.length})</p>
              {result.findings.map(f => (
                <div key={f.id} className="p-3 rounded-lg border bg-card" data-testid={`entra-finding-${f.id}`}>
                  <div className="flex items-center gap-2 mb-1"><Badge variant="outline" className={`text-[9px] uppercase ${sevBadge[f.severity]}`}>{f.severity}</Badge><p className="text-sm font-medium">{f.name}</p><span className="text-[11px] text-muted-foreground ml-auto">{f.category}</span></div>
                  <p className="text-xs text-muted-foreground">{f.description}</p>
                  {f.evidence && <p className="text-[11px] font-mono text-primary/80 mt-1">Evidence: {f.evidence}</p>}
                  <p className="text-xs mt-1.5"><span className="font-semibold">Remediation:</span> {f.remediation}</p>
                </div>
              ))}
              {result.unknown_controls?.length > 0 && (
                <div className="pt-2" data-testid="entra-unknown-controls">
                  <p className="text-sm font-medium mb-2">Needs manual verification ({result.unknown_controls.length})</p>
                  {result.unknown_controls.map(u => <div key={u.id} className="p-2.5 rounded-lg border border-dashed bg-muted/30 mb-1.5"><p className="text-sm">{u.name}</p><p className="text-[11px] text-muted-foreground">{u.evidence}</p></div>)}
                </div>
              )}
              {result.findings.length === 0 && <p className="text-sm text-green-500 flex items-center gap-2"><CheckCircle className="h-4 w-4" /> All controls enforced. Excellent posture.</p>}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
